import { useLayoutEffect, useSyncExternalStore } from "react";
import { waitForPage } from "../api/client";

/**
 * A page on its first read, where you look (owner 2026-10-21: 「画面加载只在最上面？
 * 我发现在加载的时候因为要等，用户会一直点一直按，画面加载得更慢」). The page's own
 * shape at once, in soft placeholders, and a pill at the top of it saying what
 * is being read; the menu item that opened it turns while one is on screen
 * (App, usePageReading). Approved as the prototype's PageSkel (v2.18).
 *
 * It stands in for the line each page had of its own — 在载入… 读取中… 读着…
 * 算着… 价目表读取中… — five ways of saying one thing, in small grey type where
 * nobody was looking. Shown only until a page has something: a page kept from
 * before opens on what it had while it is read again, as it did.
 */

/** The pages of the menu, by the key App and the address use. */
export type PageKey = "today" | "leads" | "customers" | "followup" | "prices" | "reports" | "board" | "playbook" | "ai" | "settings" | "users";

/** 「正在读取{word}…」 */
const WORD: Record<PageKey, string> = {
  today: "今天的数字",
  leads: "对话",
  customers: "客户",
  followup: "要跟进的客户",
  prices: "价目表",
  reports: "经营报表",
  board: "话术榜",
  playbook: "剧本",
  ai: "小爱",
  settings: "设置",
  users: "团队",
};

/** rows: a list (对话); table: a toolbar over rows; split: a list or rail beside a card (跟进, 价目表, 剧本, 设置);
    dash: tabs, four numbers, two cards; today: a strip, two cards side by side, one across. The redesign's shapes (prototype v2.20). */
type Kind = "rows" | "table" | "split" | "dash" | "today";
const KIND: Record<PageKey, Kind> = {
  today: "today",
  leads: "rows",
  customers: "table",
  followup: "split",
  prices: "split",
  reports: "dash",
  board: "dash",
  playbook: "split",
  ai: "dash",
  settings: "split",
  users: "dash",
};

// Pages with a placeholder of their own mounted, counted: a page kept hidden
// with its read still out keeps counting, and turns its item again when shown.
const showing = new Map<string, number>();
const listeners = new Set<() => void>();
function count(page: string, by: number) {
  const n = (showing.get(page) ?? 0) + by;
  if (n > 0) showing.set(page, n);
  else showing.delete(page);
  for (const f of listeners) f();
}
const listen = (f: () => void) => {
  listeners.add(f);
  return () => {
    listeners.delete(f);
  };
};

/** Whether this page is still on its first read: one of its placeholders is up. */
export function usePageReading(page: string): boolean {
  return useSyncExternalStore(listen, () => showing.has(page));
}

/** One soft bar: w and h as CSS lengths or pixels. */
function B({ w, h, round, className }: { w: string | number; h: number; round?: boolean; className?: string }) {
  return <i className={`pskel-b${round ? " round" : ""}${className ? ` ${className}` : ""}`} style={{ width: w, height: h }} />;
}

/** A list row: a face, a name and a line, something on the right. Widths vary so it reads as rows, not stripes. */
function Row({ i }: { i: number }) {
  return (
    <div className="pskel-row">
      <B w={36} h={36} round />
      <span className="pskel-lines">
        <B w={`${44 + ((i * 13) % 30)}%`} h={12} />
        <B w={`${64 + ((i * 7) % 26)}%`} h={10} />
      </span>
      <B w={48} h={20} round />
    </div>
  );
}

const rows = (n: number) => Array.from({ length: n }, (_, i) => <Row key={i} i={i} />);
const chips = (ws: number[]) => (
  <div className="pskel-chips">
    {ws.map((w, i) => (
      <B key={i} w={w} h={28} round />
    ))}
  </div>
);

export default function PageSkel({
  page,
  kind,
  word,
  bare,
}: {
  page: PageKey;
  kind?: Kind;
  /** What is read, when it is a part of the page: 素材库 on 小爱. */
  word?: string;
  /** The page draws its own heading and controls above: placeholders for its body only. */
  bare?: boolean;
}) {
  // Before the paint, so the menu item turns in the frame the placeholder comes
  // in; and the line along the top runs as long as it is up (waitForPage).
  useLayoutEffect(() => {
    count(page, 1);
    const ended = waitForPage(page);
    return () => {
      count(page, -1);
      // After this commit: one placeholder handing over to the next (the
      // page's code arriving, then the page's own) put the line out 150 ms.
      queueMicrotask(ended);
    };
  }, [page]);
  const k = kind ?? KIND[page];
  const pill = (
    <div className="pskel-pill" role="status" aria-live="polite">
      <i className="pskel-spin" aria-hidden="true" />
      正在读取{word ?? WORD[page]}…
    </div>
  );
  const head = bare ? null : (
    <div className="pskel-head">
      <B w={168} h={24} />
      <B w="min(380px, 70%)" h={12} />
    </div>
  );
  if (k === "rows")
    return (
      <div className={`pskel pskel-rows${bare ? " bare" : ""}`} aria-busy="true">
        {pill}
        {rows(9)}
      </div>
    );
  if (k === "split")
    return (
      <div className={`pskel pskel-split${bare ? " bare" : ""}`} aria-busy="true">
        {pill}
        <div className="pskel-side">
          {!bare && chips([52, 64, 48])}
          {rows(6)}
        </div>
        <div className="pskel-main pskel-card">
          <B w="36%" h={16} />
          <B w="72%" h={11} />
          <B w="58%" h={11} />
          <B w="100%" h={180} />
        </div>
      </div>
    );
  if (k === "today")
    return (
      <div className="pskel pskel-today" aria-busy="true">
        {pill}
        {head}
        <B w="100%" h={150} />
        <div className="pskel-two">
          {[0, 1].map((i) => (
            <div key={i} className="pskel-card">
              <B w="40%" h={14} />
              <B w="30%" h={30} />
              {rows(4)}
            </div>
          ))}
        </div>
        <div className="pskel-card">
          <B w="20%" h={14} />
          <B w="14%" h={30} />
          {rows(4)}
        </div>
      </div>
    );
  // 经营 (GrowthView): its three rows over the pages as tall as they are — the answer with the period beside it, the
  // product with the dates, the tabs — in the page's own padding, then 总览's shape, the hero and the dial over the four
  // numbers; the pill floats over them. Its one row of chips and the pill above it put everything 50 px off where the
  // page then drew it, and the page jumped when its code came (2026-10-28). Bare, in 总览 under the real rows: the same.
  if (page === "reports") {
    const body = (
      <div className="pskel-biz-b">
        {pill}
        <div className="pskel-two">
          <div className="pskel-card">
            <B w="30%" h={14} />
            <B w="42%" h={44} />
            <B w="100%" h={300} />
          </div>
          <div className="pskel-card">
            <B w="44%" h={14} />
            <B w="100%" h={200} />
            <B w="60%" h={30} />
            <B w="80%" h={60} />
          </div>
        </div>
        <div className="pskel-kpis">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="pskel-card">
              <B w="46%" h={11} />
              <B w="62%" h={26} />
              <B w="80%" h={10} />
            </div>
          ))}
        </div>
      </div>
    );
    if (bare)
      return (
        <div className="pskel pskel-biz bare" aria-busy="true">
          {body}
        </div>
      );
    return (
      <div className="pskel pskel-biz" aria-busy="true">
        <div className="pskel-biz-h">
          <span className="pskel-biz-t">
            <B w={88} h={30} />
            <B w="min(420px, 72%)" h={14} />
          </span>
          <B w={409} h={40} round />
        </div>
        <div className="pskel-biz-p">
          <B w={313} h={40} round className="pc" />
          <B w={121} h={36} round className="ph" />
          <B w={285} h={32} round />
        </div>
        <div className="pskel-biz-tabs">
          {[34, 34, 34, 34, 34, 34].map((w, i) => (
            <B key={i} w={w} h={16} />
          ))}
        </div>
        {body}
      </div>
    );
  }
  if (k === "dash")
    return (
      <div className={`pskel${bare ? " bare" : ""}`} aria-busy="true">
        {pill}
        {head}
        {!bare && chips([64, 72, 58, 80, 60])}
        <div className="pskel-kpis">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="pskel-card">
              <B w="46%" h={11} />
              <B w="62%" h={26} />
              <B w="80%" h={10} />
            </div>
          ))}
        </div>
        <div className="pskel-two">
          <div className="pskel-card">
            <B w="30%" h={14} />
            <B w="100%" h={190} />
          </div>
          <div className="pskel-card">
            <B w="44%" h={14} />
            {rows(4)}
          </div>
        </div>
      </div>
    );
  return (
    <div className={`pskel${bare ? " bare" : ""}`} aria-busy="true">
      {pill}
      {head}
      {!bare && (
        <div className="pskel-tool">
          <B w="min(260px, 55%)" h={34} />
          {chips([56, 64, 52])}
        </div>
      )}
      <div className="pskel-card">{rows(9)}</div>
    </div>
  );
}
